import React, { useEffect, useRef, useState } from "react";
import { useConfirm } from "@/hooks/useConfirm";
import ConfirmDialog from "@/components/ConfirmDialog";
import { Sparkles, X, Send, Terminal, RotateCcw, ChevronDown, Share2, BookmarkPlus, FileText, Bot } from "lucide-react";
import { api, streamAIChat } from "@/lib/api";
import { toast } from "sonner";
import CodeBlock from "@/components/CodeBlock";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

function parseContent(text) {
  const re = /```([a-zA-Z0-9_+-]*)\n([\s\S]*?)```/g;
  const parts = [];
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push({ type: "text", value: text.slice(last, m.index) });
    parts.push({ type: "code", lang: m[1] || "text", value: m[2] });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push({ type: "text", value: text.slice(last) });
  return parts;
}

const SESSION_KEY = "vs_ai_session";

export default function AIPanel({ onClose, onSwitchToAtena, channels, onSendToChannel, onSaveSnippet, workspaceId, initialPrompt = "" }) {
  const { ask, confirmProps } = useConfirm();
  const [sessionId] = useState(() => {
    let s = localStorage.getItem(SESSION_KEY);
    if (!s) { s = `s-${Date.now()}`; localStorage.setItem(SESSION_KEY, s); }
    return s;
  });
  const [provider, setProvider] = useState(() => localStorage.getItem("vs_ai_provider") || "internal");
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [showProviderMenu, setShowProviderMenu] = useState(false);

  // A /claude command from the palette seeds the composer.
  useEffect(() => { if (initialPrompt) setText(initialPrompt); }, [initialPrompt]);

  // Send-to-channel dialog
  const [sendTarget, setSendTarget] = useState(null);
  const [chanId, setChanId] = useState("");
  // Save-snippet dialog
  const [snippetTarget, setSnippetTarget] = useState(null);
  const [snippetTitle, setSnippetTitle] = useState("");

  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/ai/messages", { params: { session_id: sessionId } })
      .then((r) => setMessages(r.data)).catch(() => {});
  }, [sessionId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => { localStorage.setItem("vs_ai_provider", provider); }, [provider]);

  const send = async () => {
    if (!text.trim() || streaming) return;
    const prompt = text;
    setText(""); setStreaming(true);
    const userMsg = { id: `u-${Date.now()}`, role: "user", content: prompt };
    const asstMsg = { id: `a-${Date.now()}`, role: "assistant", content: "", sources: [] };
    setMessages((prev) => [...prev, userMsg, asstMsg]);
    await streamAIChat({
      prompt, session_id: sessionId, provider, workspace_id: workspaceId,
      onDelta: (d) => setMessages((prev) => prev.map((m) => m.id === asstMsg.id ? { ...m, content: m.content + d } : m)),
      onSources: (srcs) => setMessages((prev) => prev.map((m) => m.id === asstMsg.id ? { ...m, sources: srcs } : m)),
      onError: (err) => { toast.error(err); setMessages((prev) => prev.map((m) => m.id === asstMsg.id ? { ...m, content: m.content + `\n\n[error] ${err}` } : m)); },
      onDone: () => setStreaming(false),
    });
    setStreaming(false);
  };

  const doClearChat = async () => {
    try { await api.delete("/ai/messages", { params: { session_id: sessionId } }); setMessages([]); toast.success("Istoric șters"); }
    catch { toast.error("Nu a reușit"); }
  };

  const clearChat = () => {
    if (messages.length === 0) return doClearChat();
    ask({
      title: "Ștergi istoricul?", destructive: true, confirmLabel: "Șterge",
      description: "Conversația cu VIBE.AI dispare. Nu se poate anula.",
      testId: "confirm-clear-ai", onConfirm: doClearChat,
    });
  };

  const doSendToChannel = async () => {
    if (!chanId || !sendTarget) return;
    try {
      await onSendToChannel({ channel_id: chanId, content: sendTarget.content });
      toast.success(`Trimis în canal`);
      setSendTarget(null); setChanId("");
    } catch { toast.error("Nu a reușit"); }
  };

  const doSaveSnippet = async () => {
    if (!snippetTarget || !snippetTitle.trim()) return;
    try {
      await onSaveSnippet({ title: snippetTitle, language: snippetTarget.lang || "text", code: snippetTarget.code });
      toast.success("Salvat ca fragment");
      setSnippetTarget(null); setSnippetTitle("");
    } catch { toast.error("Nu a reușit"); }
  };

  const providerLabel = provider === "internal" ? "AI INTERN" : "CLAUDE (DEZACTIVAT)";

  return (
    <div className="flex flex-col h-full bg-bg relative" data-testid="ai-panel">
      <div className="scan-line" />
      <div className="h-14 px-4 flex items-center justify-between border-b border-brand/30 bg-surface">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Sparkles className="w-4 h-4 text-brand" />
            {streaming && <span className="absolute -top-1 -right-1 w-2 h-2 bg-brand rounded-full pulse-amber" />}
          </div>
          <div>
            <h3 className="font-bold tracking-tight text-fg">VIBE.AI</h3>
            <p className="text-[10px] text-fg/40 mono">terminal de vibe-coding</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button" data-testid="vibe-switch-atena" onClick={onSwitchToAtena}
            className="p-1.5 text-fg/40 hover:text-fg" title="Comută pe Atena" aria-label="Comută pe Atena"
          ><Bot className="w-3.5 h-3.5" /></button>
          <button data-testid="ai-clear-chat-button" onClick={clearChat} className="p-1.5 text-fg/40 hover:text-fg" title="Șterge istoricul" aria-label="Șterge istoricul AI">
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
          <button data-testid="ai-close-button" onClick={onClose} className="p-1.5 text-fg/40 hover:text-fg" title="Închide panoul" aria-label="Închide panoul AI">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="px-4 py-2 border-b border-line/5 flex items-center justify-between">
        <span className="label-tiny">// MODEL</span>
        <div className="relative">
          <button data-testid="ai-provider-switcher" onClick={() => setShowProviderMenu((v) => !v)} className="flex items-center gap-1.5 text-xs font-medium text-brand hover:text-brand-2">
            <Terminal className="w-3 h-3" /> {providerLabel} <ChevronDown className="w-3 h-3" />
          </button>
          {showProviderMenu && (
            <div className="absolute right-0 mt-1 w-44 bg-surface border border-line/10 z-10">
              {[
                { id: "claude", label: "Claude (găzduit)", note: "dezactivat în self-hosted" },
                { id: "internal", label: "AI intern", note: "Ollama / vLLM — din Setări" },
              ].map((opt) => (
                <button key={opt.id} data-testid={`ai-provider-option-${opt.id}`} onClick={() => { setProvider(opt.id); setShowProviderMenu(false); }}
                  className={`w-full text-left px-3 py-2 text-xs hover:bg-fg/5 ${provider === opt.id ? "text-brand" : "text-fg/80"}`}>
                  <div className="font-medium">{opt.label}</div>
                  <div className="text-[10px] text-fg/40 mono">{opt.note}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && (
          <div className="text-center text-fg/50 text-sm py-8">
            <p className="label-tiny text-brand mb-3">// VIBE.AI GATA</p>
            <p className="mb-4 text-fg/60">Spune ce vrei să construiești.<br/>Scriu eu codul.</p>
            <div className="space-y-2 text-left">
              {[
                "Fă o listă de sarcini în React, cu localStorage",
                "Endpoint FastAPI pentru încărcarea unei imagini",
                "Explică async/await în Python",
              ].map((q) => (
                <button key={q} data-testid={`ai-suggestion-${q.slice(0,10)}`} onClick={() => setText(q)}
                  className="block w-full text-xs px-3 py-2 bg-surface border border-line/10 hover:border-brand/40 hover:text-brand transition-colors mono text-left">
                  $ {q}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m) => (
          <AIMessage
            key={m.id} m={m} streaming={streaming}
            onSendToChannel={() => setSendTarget(m)}
            onSaveSnippet={(lang, code) => { setSnippetTarget({ lang, code }); setSnippetTitle("Fragment din VibeAI"); }}
          />
        ))}
      </div>

      <div className="border-t border-brand/30 bg-surface p-3">
        <div className="bg-surface border border-line/10 focus-within:border-brand/50">
          <textarea
            data-testid="ai-input" value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="$ spune ce să construiesc…" rows={2}
            className="w-full bg-transparent text-fg text-sm p-3 outline-none resize-none mono placeholder-fg/30"
            disabled={streaming}
          />
          <div className="flex items-center justify-between px-3 pb-2">
            <span className="label-tiny text-brand/60">
              {streaming ? "generating..." : "ready"}<span className="cursor-blink ml-1">▋</span>
            </span>
            <button data-testid="ai-send-button" onClick={send} disabled={streaming || !text.trim()}
              className="flex items-center gap-1.5 px-3 py-1 bg-accent-gradient text-fg font-medium text-sm hover:opacity-90 disabled:opacity-40">
              {streaming ? "..." : "Rulează"}<Send className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>

      {/* Send to channel dialog */}
      <Dialog open={!!sendTarget} onOpenChange={(o) => !o && setSendTarget(null)}>
        <DialogContent className="bg-surface border-line/10 text-fg rounded-none"
          description="Trimite răspunsul într-un canal, ca mesaj.">
          <DialogHeader><DialogTitle className="font-bold tracking-tight">Trimite într-un canal</DialogTitle></DialogHeader>
          <Select value={chanId} onValueChange={setChanId}>
            <SelectTrigger data-testid="ai-share-channel-select" className="bg-surface-2 border-line/10 rounded-none">
              <SelectValue placeholder="-- alege un canal --" />
            </SelectTrigger>
            <SelectContent className="bg-surface border-line/10 text-fg rounded-none">
              {(channels || []).map((c) => (
                <SelectItem key={c.id} value={c.id} className="focus:bg-fg/10">#{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-fg/40 mono">Colegii îl vor vedea ca pe un mesaj de la tine.</p>
          <DialogFooter><button data-testid="ai-share-confirm-button" onClick={doSendToChannel} disabled={!chanId} className="btn-primary">Trimite</button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save as snippet dialog */}
      <Dialog open={!!snippetTarget} onOpenChange={(o) => !o && setSnippetTarget(null)}>
        <DialogContent className="bg-surface border-line/10 text-fg rounded-none"
          description="Salvează răspunsul ca fragment de cod, cu titlu și limbaj.">
          <DialogHeader><DialogTitle className="font-bold tracking-tight">Salvează ca fragment</DialogTitle></DialogHeader>
          <input data-testid="ai-snippet-title-input" autoFocus className="input-flat" placeholder="Titlul fragmentului" value={snippetTitle} onChange={(e) => setSnippetTitle(e.target.value)} />
          {snippetTarget && (
            <div className="text-[11px] text-fg/40 mono">Limba: <span className="text-brand">{snippetTarget.lang}</span></div>
          )}
          <DialogFooter><button data-testid="ai-snippet-save-button" onClick={doSaveSnippet} disabled={!snippetTitle.trim()} className="btn-primary">Salvează</button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AIMessage({ m, streaming, onSendToChannel, onSaveSnippet }) {
  const isUser = m.role === "user";
  const parts = parseContent(m.content);
  const codeParts = parts.filter((p) => p.type === "code");

  return (
    <div className="fade-in" data-testid={`ai-message-${m.id}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <span className={`label-tiny ${isUser ? "text-fg/40" : "text-brand"}`}>
          {isUser ? "// TU" : "// VIBE.AI"}
        </span>
      </div>
      <div className={`text-sm space-y-2 ${isUser ? "text-fg/80 pl-3 border-l border-line/10" : "text-fg/90 pl-3 border-l border-brand/40"}`}>
        {parts.map((p, i) =>
          p.type === "code"
            ? (
              <div key={i} className="space-y-1">
                <CodeBlock lang={p.lang} code={p.value} compact />
                {!isUser && !streaming && (
                  <button
                    data-testid={`ai-save-snippet-${i}`}
                    onClick={() => onSaveSnippet?.(p.lang, p.value)}
                    className="text-[10px] mono text-fg/40 hover:text-brand flex items-center gap-1"
                  >
                    <BookmarkPlus className="w-3 h-3" /> salvează ca fragment
                  </button>
                )}
              </div>
            )
            : <p key={i} className="whitespace-pre-wrap break-words leading-relaxed">{p.value}</p>
        )}
        {!isUser && m.sources?.length > 0 && (
          <p className="text-[10px] mono text-fg/40 flex flex-wrap items-center gap-1" title="Documente din workspace folosite drept context">
            <FileText className="w-3 h-3" /> surse: {m.sources.join(", ")}
          </p>
        )}
        {!isUser && streaming && m.content === "" && (
          <p className="text-brand mono text-xs">gândește<span className="cursor-blink">_</span></p>
        )}
        {!isUser && !streaming && m.content && (
          <button data-testid={`ai-share-trigger-${m.id}`} onClick={onSendToChannel} className="text-[11px] mono text-fg/40 hover:text-brand flex items-center gap-1 mt-2">
            <Share2 className="w-3 h-3" /> trimite în canal
          </button>
        )}
      </div>
      <ConfirmDialog {...confirmProps} />
    </div>
  );
}
